import FontBody1 from "../../../../../components/fonts/font-body1/font-body1";
import FontBody2 from "../../../../../components/fonts/font-body2/font-body2";
import FontBody3 from "../../../../../components/fonts/font-body3/font-body3";
import { FONT_COLOR, FONT_VARIANT } from "../../../../../components/fonts/font-props";
import { FormFieldLine } from "../../../../../components/form-field-line/form-field-line";
import { Toggle } from "../../../../../components/inputs/toggle/toggle";
import Card from "../../../../../components/layouts/card/card";
import DashTranslation from "../../../../../dashboard-translation/dashboard-translaton";
import useDashboardTranslation from "../../../../../dashboard-translation/hooks/dashboard-translation-hook";
import currentPortalStore from "../../../../../stores/current-portal-store";
import { IOption, SelectDropdown } from "../../../../../components/inputs/select-dropdown/select-dropdown";
import {
	BXGY_COMPENSATE_BY,
	BXGY_COMPENSATE_BY_MAP,
	CAP_PRICE_DIFF_CALC_BY,
	CAP_PRICE_DIFF_CALC_BY_MAP,
} from "../../../../../models/User";
import { SingleValue } from "react-select";
import { FEATURES } from "../../../../../models/Plan";
import { Note } from "../../../../../components/note/note";
import React from "react";
import ResolveCapability from "../../../../../components/resolve-capability/resolve-capability";
import { PROFILE_CAPABILITIES } from "../../../../../models/ProfileCapabilities";

interface IProps {
    onExpandClick: (expanded: boolean) => void;
    isExpanded: boolean;
    dataName?: string; /** used by Candu to target an element */
}

const DiscountCard: React.FC<IProps> = ({ onExpandClick, isExpanded, dataName }) => {
    const { getTranslation } = useDashboardTranslation();
    const pageScope = 'dashboard-settings-store';

    const storeSetting = currentPortalStore.setting;
    const isBxgyFeatureAllowed = storeSetting && currentPortalStore.isFeatureAllowed(FEATURES.BXGY)

    const bxgyCompensateByOptions = [
        {
            label: BXGY_COMPENSATE_BY_MAP[BXGY_COMPENSATE_BY.DEDUCT_Y_FROM_X].label,
            value: BXGY_COMPENSATE_BY.DEDUCT_Y_FROM_X
        },
    ];

    const bxgyCompensateBy = storeSetting?.bxgyCompensateBy ? {
        value: storeSetting.bxgyCompensateBy,
        label: BXGY_COMPENSATE_BY_MAP[storeSetting.bxgyCompensateBy].label
    } : undefined;

    const onChangeBxgyCompensateBy = (value: SingleValue<IOption>) => {
        storeSetting?.setBxgyCompensateBy(value?.value as BXGY_COMPENSATE_BY);
    }
	
    const capPriceDiffCalcOptions = [
        {
            label: getTranslation(CAP_PRICE_DIFF_CALC_BY_MAP[CAP_PRICE_DIFF_CALC_BY.COMPARE_AT_PRICE].label, pageScope),
            value: CAP_PRICE_DIFF_CALC_BY.COMPARE_AT_PRICE
        },
        {
            label: getTranslation(CAP_PRICE_DIFF_CALC_BY_MAP[CAP_PRICE_DIFF_CALC_BY.CATALOG_PRICE].label, pageScope),
            value: CAP_PRICE_DIFF_CALC_BY.CATALOG_PRICE
        }
    ];

    const capProductExchangePriceDiffCalcBy = storeSetting?.capDiscountStore.productExchangePriceDiffCalcBy ? {
        value: storeSetting?.capDiscountStore.productExchangePriceDiffCalcBy,
        label: getTranslation(CAP_PRICE_DIFF_CALC_BY_MAP[storeSetting?.capDiscountStore.productExchangePriceDiffCalcBy].label, pageScope)
    } : undefined;

    const capVariantExchangePriceDiffCalcBy = storeSetting?.capDiscountStore.variantExchangePriceDiffCalcBy ? {
        value: storeSetting.capDiscountStore.variantExchangePriceDiffCalcBy,
        label: getTranslation(CAP_PRICE_DIFF_CALC_BY_MAP[storeSetting.capDiscountStore.variantExchangePriceDiffCalcBy].label, pageScope)
    } : undefined;

    const onChangeCapProductExchangePriceDiffCalcBy = (value: SingleValue<IOption>) => {
        storeSetting?.capDiscountStore.setProductExchangePriceDiffCalcBy(value?.value as CAP_PRICE_DIFF_CALC_BY);
    }

    const onChangeCapVariantExchangePriceDiffCalcBy = (value: SingleValue<IOption>) => {
        storeSetting?.capDiscountStore.setVariantExchangePriceDiffCalcBy(value?.value as CAP_PRICE_DIFF_CALC_BY);
    }

    return <Card
        toolbar={{
            title: (
                <FontBody1 variant={FONT_VARIANT.BOLD}>
                    <DashTranslation translationKey="DISCOUNT" scope={pageScope} />
                </FontBody1>
            )
        }}
        expandCallback={onExpandClick}
        isExpanded={isExpanded}
        dataName={dataName}
    >
        <FontBody2 variant={FONT_VARIANT.BOLD} color={FONT_COLOR.PRIMARY2}>
            {getTranslation('BXGY', pageScope)}
        </FontBody2>

        <FormFieldLine
            label={getTranslation('ALLOW_BXGY_DEALS', pageScope)}
            note={getTranslation('WHEN_ON_PORTAL_PROCESSES_EXCHANGE_AND_REFUND_BY_PAID_PRICE', pageScope)}
            dataName="allow-bxgy-deals"
        >
            <Toggle
                checked={storeSetting?.isBxgyEnabled}
                onChange={storeSetting?.setIsBxgyEnabled}
                label=''
            />
        </FormFieldLine>

        <FormFieldLine
            label={getTranslation('BXGY_BREAKABLE', pageScope)}
            disabled={!isBxgyFeatureAllowed}
            dataName="bxgy-breakable"
        >
            <Toggle
                disabled={true}
                checked={storeSetting?.bxgyBreakable}
                onChange={storeSetting?.setBxgyBreakable}
                label=''
            />
        </FormFieldLine>

        <FormFieldLine
            label={getTranslation('BXGY_COMPENSATE_BY', pageScope)}
            note={getTranslation('BXGY_COMPENSATE_BY_NOTE', pageScope)}
            disabled={!isBxgyFeatureAllowed}
            dataName="bxgy-compensate-by"
        >
            <SelectDropdown
                disabled={true}
                options={bxgyCompensateByOptions}
                value={bxgyCompensateBy}
                onChange={onChangeBxgyCompensateBy}
            />
        </FormFieldLine>

        {!isBxgyFeatureAllowed && <Note body={getTranslation('BXGY_NO_ADDON', pageScope)} />}

	    <ResolveCapability capability={PROFILE_CAPABILITIES.COMPARE_AT_PRICE}>
		    {({ isSupported, isFeatureEnabled, isProductExchangeSupported, isVariantExchangeSupported }) => isSupported && (
				<>
					<FontBody2 variant={FONT_VARIANT.BOLD} color={FONT_COLOR.PRIMARY2}>
						{getTranslation('COMPARE_AT_PRICE', pageScope)}
						<br />
						<FontBody3>
							<DashTranslation translationKey="CAP_PRICE_DIFF_CALC_BY_DESCRIPTION" scope={pageScope} />
						</FontBody3>
					</FontBody2>

					{isProductExchangeSupported && (
						<FormFieldLine
							label={getTranslation('PRODUCT_EXCHANGE', pageScope)}
							note={getTranslation('CAP_PRICE_DIFF_CALC_BY', pageScope)}
							disabled={!isFeatureEnabled}
							dataName="product-exchange-cap"
						>
							<SelectDropdown
								options={capPriceDiffCalcOptions}
								value={capProductExchangePriceDiffCalcBy}
								onChange={onChangeCapProductExchangePriceDiffCalcBy}
								disabled={!isFeatureEnabled}
							/>
						</FormFieldLine>
					)}

					{isVariantExchangeSupported && (
						<FormFieldLine
							label={getTranslation('VARIANT_EXCHANGE', pageScope)}
							note={getTranslation('CAP_PRICE_DIFF_CALC_BY', pageScope)}
							disabled={!isFeatureEnabled}
							dataName="variant-exchange-cap"
						>
							<SelectDropdown
								options={capPriceDiffCalcOptions}
								value={capVariantExchangePriceDiffCalcBy}
								onChange={onChangeCapVariantExchangePriceDiffCalcBy}
								disabled={!isFeatureEnabled}
							/>
						</FormFieldLine>
					)}

					{!isFeatureEnabled && <Note body={getTranslation('CAP_NO_ADDON', pageScope)} />}
				</>
		    )}
	    </ResolveCapability>

    </Card>
}

export default DiscountCard;
